import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../ui/Button';
import analyticsService from '../../../utils/analyticsService';

// Import our reusable components
import { LoadingSpinner, ErrorMessage, NoDataMessage } from './StateComponents';
import TrendItem from './TrendItem';
import InsightCard from './InsightCard';
import OpportunityCard from './OpportunityCard';

const PerformanceInsights = () => {
  const { userProfile } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch real insights data from Supabase
  useEffect(() => {
    const fetchInsightsData = async () => {
      if (!userProfile?.id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const insightsData = await analyticsService.getPerformanceInsights(userProfile.id);
        setInsights(insightsData);
      } catch (err) {
        console.error('Error fetching insights data:', err);
        setError('Failed to load insights data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchInsightsData();
  }, [userProfile?.id]);

  // Memoized utility functions to prevent recreation on every render
  const utils = useMemo(() => ({
    getImpactColor: (impact) => {
      switch (impact) {
        case 'positive': return 'text-green-600 bg-green-50 border-green-200';
        case 'negative': return 'text-red-600 bg-red-50 border-red-200';
        default: return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      }
    },
    
    getImpactIcon: (impact) => {
      switch (impact) {
        case 'positive': return '✅';
        case 'negative': return '⚠️';
        default: return '💡';
      }
    },
    
    getPotentialColor: (potential) => {
      switch (potential) {
        case 'high': return 'text-green-600';
        case 'medium': return 'text-yellow-600';
        default: return 'text-blue-600';
      }
    }
  }), []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={() => window.location.reload()} />;
  if (!insights) return <NoDataMessage message="We don't have enough data to generate insights yet. Continue using the platform to get personalized recommendations." />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Performance Insights</h2>
        <p className="text-gray-600">AI-powered recommendations to boost your success</p>
      </div>

      {/* Performance Trends */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Trends</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {insights.trends.map((period, index) => (
            <div key={index} className="space-y-3">
              <h4 className="font-medium text-gray-700">{period.period}</h4>
              <div className="grid grid-cols-2 gap-3">
                <TrendItem 
                  label="Applications" 
                  value={period.applications.value} 
                  trend={period.applications.trend} 
                  change={period.applications.change} 
                />
                <TrendItem 
                  label="Responses" 
                  value={period.responses.value} 
                  trend={period.responses.trend} 
                  change={period.responses.change} 
                />
                <TrendItem 
                  label="Interviews" 
                  value={period.interviews.value} 
                  trend={period.interviews.trend} 
                  change={period.interviews.change} 
                />
                <TrendItem 
                  label="Hires" 
                  value={period.hires.value} 
                  trend={period.hires.trend} 
                  change={period.hires.change} 
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Insights Grid - 2 columns on desktop, 1 on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strengths */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Strengths</h3>
          <div className="space-y-4">
            {insights.strengths && insights.strengths.length > 0 ? (
              insights.strengths.map(strength => (
                <InsightCard 
                  key={strength.id} 
                  item={strength} 
                  impactColor={utils.getImpactColor} 
                  impactIcon={utils.getImpactIcon} 
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No strengths identified yet</p>
                <p className="text-xs mt-1">Apply to more jobs to discover your strengths</p>
              </div>
            )}
          </div>
        </div>

        {/* Areas for Improvement */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Areas for Improvement</h3>
          <div className="space-y-4">
            {insights.improvements && insights.improvements.length > 0 ? (
              insights.improvements.map(improvement => (
                <InsightCard 
                  key={improvement.id} 
                  item={improvement} 
                  impactColor={utils.getImpactColor} 
                  impactIcon={utils.getImpactIcon} 
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">Great job! No areas for improvement identified</p>
                <p className="text-xs mt-1">Keep up the excellent work</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Growth Opportunities - Full width */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Growth Opportunities</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights.opportunities && insights.opportunities.length > 0 ? (
            insights.opportunities.map(opportunity => (
              <OpportunityCard 
                key={opportunity.id} 
                opportunity={opportunity} 
                potentialColor={utils.getPotentialColor} 
              />
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              <p className="text-sm">No growth opportunities identified yet</p>
              <p className="text-xs mt-1">Keep applying to jobs to discover skill development opportunities</p>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <Button>📚 View Learning Resources</Button>
        <Button variant="outline">🎯 Set Performance Goals</Button>
        <Button variant="outline">📊 Schedule Performance Review</Button>
      </div>
    </div>
  );
};

export default PerformanceInsights;
